import { useState, useMemo } from 'react';
import { calculatePagination, getPaginationConfig } from '../config/pagination';

/**
 * Hook personnalisé pour gérer la pagination des tableaux
 * @param {Array} data - Données à paginer
 * @param {string} pageType - Type de page pour la configuration
 * @param {object} options - Options de pagination
 * @returns {object} État et fonctions de pagination
 */
export const usePagination = (data, pageType, options = {}) => {
  const config = getPaginationConfig(pageType);
  
  const [currentPage, setCurrentPage] = useState(options.initialPage || 1);
  const [pageSize, setPageSize] = useState(options.initialPageSize || config.pageSize);
  
  // Calculer la pagination
  const pagination = useMemo(() => {
    return calculatePagination(data, currentPage, pageSize);
  }, [data, currentPage, pageSize]);
  
  // Fonctions de navigation
  const goToPage = (page) => {
    const validPage = Math.max(1, Math.min(page, pagination.totalPages));
    setCurrentPage(validPage);
  };
  
  const goToNextPage = () => {
    if (pagination.hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  const goToPreviousPage = () => {
    if (pagination.hasPreviousPage) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  const goToFirstPage = () => {
    setCurrentPage(1);
  };
  
  const goToLastPage = () => {
    setCurrentPage(pagination.totalPages);
  };
  
  // Changer la taille de page
  const changePageSize = (newPageSize) => {
    setPageSize(newPageSize);
    // Recalculer la page actuelle pour maintenir approximativement la même position
    const currentItemIndex = (currentPage - 1) * pageSize;
    const newPage = Math.max(1, Math.ceil((currentItemIndex + 1) / newPageSize));
    setCurrentPage(newPage);
  };
  
  // Reset pagination (utile lors de changements de filtres)
  const resetPagination = () => {
    setCurrentPage(1);
  };
  
  return {
    // Données paginées
    paginatedData: pagination.data,
    
    // Informations de pagination
    totalItems: pagination.totalItems,
    totalPages: pagination.totalPages,
    currentPage: pagination.currentPage,
    pageSize: pagination.pageSize,
    startIndex: pagination.startIndex,
    endIndex: pagination.endIndex,
    
    // États de navigation
    hasNextPage: pagination.hasNextPage,
    hasPreviousPage: pagination.hasPreviousPage,
    isFirstPage: pagination.isFirstPage,
    isLastPage: pagination.isLastPage,
    
    // Fonctions de navigation
    goToPage,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    
    // Gestion de la taille de page
    changePageSize,
    
    // Utilitaires
    resetPagination,
    
    // Configuration
    config
  };
};